"use client";
import { useEffect } from "react";
import dynamic from "next/dynamic";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { useMediaQuery, useTheme, useColorScheme } from "@mui/material";

import { observer, useStore } from "../../service/mobx";

const Activate = dynamic(() =>
  import("../../component/Dialog/dialogs/appWide/Activate")
);

function HooksAppWide() {
  const { analytics, bottomSheet, device, dialog, layout, reader, user } =
    useStore();
  const theme = useTheme();
  const { setMode } = useColorScheme();
  const isCompact = useMediaQuery(theme.breakpoints.only("compact"));
  const isMedium = useMediaQuery(theme.breakpoints.only("medium"));
  const isExpanded = useMediaQuery(theme.breakpoints.only("expanded"));
  const isLarge = useMediaQuery(theme.breakpoints.only("large"));
  const isXL = useMediaQuery(theme.breakpoints.only("extraLarge"));
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const whiteLabel = searchParams.get("_c");
  const [, route] = pathname.split("/");
  const url = `${pathname}${
    searchParams.size === 0 ? "" : "?" + searchParams.toString()
  }`;
  const openOnboarding = searchParams.get("onboard") === "1";
  const size = isCompact
    ? "compact"
    : isMedium
    ? "medium"
    : isExpanded
    ? "expanded"
    : isLarge
    ? "large"
    : isXL
    ? "extraLarge"
    : "";

  useEffect(() => {
    device.set.size(size);
  }, [device, size]);

  useEffect(() => {
    try {
      const namespace = "history";
      const history = JSON.parse(sessionStorage.getItem(namespace) || "[]");
      const updatedHistory = new Set([url, ...history]);

      sessionStorage.setItem(
        namespace,
        JSON.stringify([...updatedHistory].slice(0, 10))
      );
    } catch (error) {
      console.error(error);
    }
  }, [url]);

  useEffect(() => {
    if (device.isDesktop === false) {
      const onScroll = () => {
        if (device.scrolling === false) {
          device.set.scrolling(true);
        }
      };
      const onScrollEnd = () => {
        if (device.scrolling) {
          setTimeout(device.set.scrolling, 500, false);
        }
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("scrollend", onScrollEnd, { passive: true });

      return () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("scrollend", onScrollEnd);
      };
    }
  }, [device, device.isDesktop]);

  useEffect(() => {
    switch (route) {
      case "": {
        bottomSheet.set.open();
        layout.set.mountAgent(false);
        layout.set.mountContent(false);
        layout.set.maxWidth(
          device.isDesktop || user.isAnonymous === false ? false : "large"
        );
        layout.set.navOpen(device.isDesktop && user.isAnonymous === false);

        return layout.set.navOpen;
      }
      case "agent": {
        // set context
        layout.set.mountAgent(true);
        layout.set.mountContent(false);
        layout.set.maxWidth("expanded");
        break;
      }
      case "search":
      case "model":
      case "docs": {
        layout.set.mountAgent(true);
        layout.set.mountContent(true);
        layout.set.maxWidth("large");
        break;
      }
    }
  }, [
    analytics,
    bottomSheet,
    layout,
    device.isDesktop,
    user.isAnonymous,
    route
  ]);

  useEffect(() => {
    // const [whiteLabelled, setWhiteLabelled] = useState(_c);
    if (whiteLabel) {
      try {
        const { disable, related = [] } = JSON.parse(atob(whiteLabel));
        var successSignal = "1";

        reader.set.config({
          disable: disable ? new Set(disable) : undefined,
          related: new Set([...related, "authors", "audio", "conference"])
        });

        [
          document.getElementById("pane"),
          document.getElementById("rail")
        ].forEach(element => {
          if (element) {
            element.style.display = "none";
          }
        });

        document.body.style.border = "1px solid rgb(238, 238, 238)";
        // sessionStorage.setItem("whitelabelled", whiteLabel);
      } catch (error) {
        successSignal = "0";
        console.error(error);
      } finally {
        setMode("light");
        analytics.track.event("Whitelabel");
        setTimeout(window.parent?.postMessage, 2e3, successSignal, "*");
      }
    }

    layout.set.checkedWhiteLabel(true);
  }, [analytics, reader, layout, setMode, whiteLabel]);

  // ask users to fill out profile
  useEffect(() => {
    if (
      openOnboarding ||
      (user.loaded &&
        user.isAnonymous === false &&
        user.profile.developer === undefined &&
        // the user will not be able to make a successful request to a model until their plan is activated, so we wait for their plan to be active
        user.profile.plan !== undefined &&
        dialog.open === false)
    ) {
      dialog.configure({
        fullScreen: true,
        Content: Activate,
        state: { subtle: true },
        sx: {
          firstStack: {
            height: "100dvh",
            overflow: "auto"
          },
          paper: {
            p: 0,
            bgcolor: "var(--surface-dim)",
            transition: "all 1s var(--motion-easing-emphasized)"
          }
        }
      });

      if (openOnboarding) {
        const searchParams = new URLSearchParams(location.search);

        searchParams.delete("onboard");

        router.replace(
          `${location.pathname}${
            searchParams.size === 0 ? "" : "?" + searchParams.toString()
          }`
        );
      }
    }
  }, [
    dialog,
    user.loaded,
    user.isAnonymous,
    dialog.open,
    user.profile.developer,
    user.profile.plan,
    router,
    openOnboarding
  ]);
}

export default observer(HooksAppWide);

export function useWhiteLabelling() {
  const { reader } = useStore();
  const searchParams = useSearchParams();

  return reader.whiteLabelled || searchParams.has("_c");
}

export const MountAsBottomSheet = observer(function MountAsBottomSheet({
  children,
  isWhiteLabelled = false
}) {
  const { device, bottomSheet } = useStore();
  const router = useRouter();

  useEffect(() => {
    if (device.isPhone && isWhiteLabelled === false) {
      if (bottomSheet.peek) {
        setTimeout(bottomSheet.set.expanded, 200, true);
      }

      bottomSheet.set.peek(true);
      bottomSheet.set.sx({ height: "95vh" });
      bottomSheet.set.Component(() => children);
      bottomSheet.set.open(true);
    }
  }, [bottomSheet, router, isWhiteLabelled, device.isPhone, children]);

  return device.isPhone && isWhiteLabelled === false ? null : children;
});

export function useTrackView(view) {
  const { analytics, user } = useStore();

  useEffect(() => {
    if (user.loaded) {
      // set context
      analytics.set.context(view, "View");
      // remove context when component unmounts
      return analytics.set.context;
    }
  }, [analytics, user.loaded, view]);
}
